/**
 * How the score got to where it is: rule score → AI adjustment → final.
 *
 * What this is NOT, and must never become: a decomposition of the score into
 * coverage, defect and bonus. Those inputs — core coverage, reported fraction,
 * breadth, per-code defect mass, cluster collapse, the reconciliation bonus —
 * are computed inside the backend scorer and appear in NO payload the console
 * receives. A waterfall over them would be invented numbers.
 *
 * The one movement the payload does describe is the AI adjustment, and it is
 * worth showing because the direction is counter-intuitive: a reviewer agreeing
 * a figure is genuine RELIEVES the penalty, and one calling it an extraction
 * error AMPLIFIES it. So the arrow can point either way, and the caption says
 * which happened.
 *
 * Renders only when `quality_score_basis` names the adjusted score. The backend
 * can compute an adjustment and still rule that the deterministic score
 * governs; parading a number it decided against would invite a reader to weigh
 * a rejected figure as a second opinion.
 */

import { ArrowRight, Sparkles } from 'lucide-react';
import Tooltip from '../../ui/Tooltip';
import type { ValidationSummary } from '../../../types/dataQuality';
import { formatQualityScore } from '../utils/dataQualityFormat';
import { agentDelta } from './dqSelectors';
import { scoreBand } from './dqTokens';

export interface ScoreCompositionBarProps {
  summary: ValidationSummary;
}

function Stop({
  caption,
  value,
  muted,
  className = '',
}: {
  caption: string;
  value: string;
  muted?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-wider text-neutral-400">{caption}</p>
      <p
        className={`font-mono text-lg tabular-nums ${
          muted ? 'text-neutral-500' : 'font-semibold text-neutral-900'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

export default function ScoreCompositionBar({ summary }: ScoreCompositionBarProps) {
  const delta = agentDelta(summary);
  const adjusted = summary.quality_score_agent_adjusted;

  // No adjustment governs this run — the ring already carries the rule score.
  if (delta == null || adjusted == null) return null;

  // The reviewer looked and changed nothing. Worth saying out loud: it reads as
  // agreement, where silence would read as a review that never happened.
  const unchanged = delta === 0;
  const relieved = delta > 0;
  const coverage = summary.agent_coverage;
  const hasCoverage = coverage != null && Number.isFinite(coverage);
  const coveragePct = hasCoverage ? Math.round((coverage as number) * 100) : null;
  const band = scoreBand(adjusted);

  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50/60 p-4">
      <p className="mb-3 text-xs font-bold uppercase tracking-widest text-neutral-400">
        How this score was reached
      </p>

      <div className="flex flex-wrap items-end gap-4">
        <Stop caption="Rule score" value={formatQualityScore(summary.quality_score)} muted />

        <ArrowRight className="mb-1.5 h-4 w-4 shrink-0 text-neutral-300" aria-hidden="true" />

        <div>
          <p className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-neutral-400">
            <Sparkles className="h-3 w-3 text-secondary-500" aria-hidden="true" />
            AI adjustment
          </p>
          <p
            className={`font-mono text-lg font-semibold tabular-nums ${
              unchanged
                ? 'text-neutral-500'
                : relieved
                  ? 'text-emerald-600'
                  : 'text-red-600'
            }`}
          >
            {/* No signed zero: "−0.00" claims a direction that did not happen. */}
            {unchanged ? 'none' : `${relieved ? '+' : '−'}${Math.abs(delta).toFixed(2)}`}
          </p>
        </div>

        <ArrowRight className="mb-1.5 h-4 w-4 shrink-0 text-neutral-300" aria-hidden="true" />

        <Stop
          caption="Authoritative"
          value={formatQualityScore(adjusted)}
          className="rounded-lg bg-white px-3 py-1 shadow-sm"
        />

        <span className={`mb-1.5 text-xs font-semibold ${band.text}`}>{band.label}</span>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-neutral-600">
        {unchanged
          ? 'The review did not change the score — the reviewer examined the findings and left the rules’ verdict standing.'
          : relieved
            ? 'The reviewer judged findings genuine rather than extraction faults, which reduced the penalty. The reviewed score is the authoritative one.'
            : 'The reviewer judged findings to be extraction faults, which increased the penalty. The reviewed score is the authoritative one.'}
      </p>

      {/* "0.72 after review" means something very different at 8% coverage than
          at 100%, so the caveat always travels with the adjustment. */}
      {coveragePct != null && (
        <div className="mt-3">
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] uppercase tracking-wider text-neutral-400">
              Findings reviewed
            </span>
            <span className="font-mono text-xs tabular-nums text-neutral-700">
              {coveragePct}%
            </span>
          </div>
          <Tooltip
            content={
              coveragePct < 100
                ? `The reviewer saw ${coveragePct}% of the findings by weight; the rest were scored by the rules alone.`
                : 'The reviewer saw every finding that carries score weight.'
            }
            position="top"
          >
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-200">
              <div
                className="h-full rounded-full bg-secondary-500"
                style={{ width: `${Math.max(2, coveragePct)}%` }}
              />
            </div>
          </Tooltip>
          {coveragePct < 100 && (
            <p className="mt-1 text-[11px] text-neutral-500">
              The remainder was scored by the rules alone.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
